import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import OwlCarousel from 'react-owl-carousel';
import 'owl.carousel/dist/assets/owl.carousel.css';
import 'owl.carousel/dist/assets/owl.theme.default.css';
import { FaStar, FaQuoteLeft } from 'react-icons/fa';

const Reviews = () => {
  const [reviewsData, setReviewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedReviews, setExpandedReviews] = useState({});
  const { t } = useTranslation();
  const reviewsRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (reviewsRef.current) observer.observe(reviewsRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        console.log('Fetching reviews from API...');
        const response = await fetch('https://hub.mileageriot.com/api/reviews');
        
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        
        const data = await response.json();
        console.log('API Response for reviews:', data);
        
        // Handle different API response formats
        const reviews = Array.isArray(data) ? data : (data.data || []);
        setReviewsData(reviews);
        setError(null);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching reviews data:', error);
        setError('Failed to load reviews');
        setReviewsData([]);
        setLoading(false);
      }
    };
    
    fetchReviews();
  }, []);

  const toggleExpanded = (index) => {
    setExpandedReviews(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const isTextLong = (text, maxLength = 150) => {
    return text && text.length > maxLength;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU');
    } catch (error) {
      return '';
    }
  };

  const renderCarousel = () => {
    if (reviewsData && reviewsData.length > 0) {
      return (
        <OwlCarousel
          className='owl-theme reviews-carousel'
          loop={reviewsData.length > 3}
          margin={30}
          dots={true}
          nav={true}
          autoplay={true}
          autoplayTimeout={5000}
          autoplaySpeed={800}
          autoplayHoverPause={true}
          responsive={{
            0: {
              items: 1
            },
            768: {
              items: 2
            },
            992: {
              items: 3
            }
          }}
        >
          {reviewsData.map((review, index) => {
            const isExpanded = expandedReviews[index];
            const displayText = isExpanded ? review.message : truncateText(review.message);
            
            return (
              <div className="item" key={review.id || index}>
                <div className="card glass-container h-100 review-card-modern">
                  <div className="p-4 d-flex flex-column">
                    {/* User info - Centered */}
                    <div className="user-info d-flex flex-column align-items-center">
                      <img 
                        src={review.avatar}
                        alt={review.name || 'User'}
                        className="rounded-circle border border-primary user-avatar mb-3"
                        style={{ width: '70px', height: '70px', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent('Mileage Riot') + '&color=00cfe8&background=1d1e26&size=128';
                        }}
                      />
                      <h6 className="text-white mb-1 user-name text-center">
                        {review.name || t('reviews.anonymous')}
                      </h6>
                      <small className="text-primary opacity-75">
                        {formatDate(review.created_at)}
                      </small>
                    </div>
                    
                    {/* Review message - Centered with quotes */}
                    <div className="review-content flex-grow-1 d-flex flex-column justify-content-center">
                      <div className="review-message text-white position-relative">
                        <FaQuoteLeft className="quote-icon" />
                        {displayText || (t('reviews.noMessage') || 'No message provided.')}
                      </div>
                      
                      {/* View more/less button - Centered */}
                      {isTextLong(review.message) && (
                        <div className="text-center mt-3">
                          <button 
                            className="btn btn-link text-primary p-0 border-0 text-decoration-none"
                            onClick={() => toggleExpanded(index)}
                            style={{ fontSize: '0.9rem', fontWeight: '500' }}
                          >
                            {isExpanded ? 
                              (t('reviews.viewLess') || 'Скрыть') : 
                              (t('reviews.viewMore') || 'Показать больше')
                            }
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </OwlCarousel>
      );
    }
    return null;
  };

  // Don't render if no reviews
  if (!loading && !error && (!reviewsData || reviewsData.length === 0)) {
    return null;
  }

  return (
    <section className="reviews-modern py-5" ref={reviewsRef}>
      <div className="container">
        {/* Section Header */}
        <div className="section-header text-center mb-5 fade-in-up stagger-1">
          <div className="section-badge">
            <FaStar className="me-2" />
            <span>Отзывы</span>
          </div>
          <h2 className="section-title text-glow text-white">
            Отзывы
          </h2>
          <div className="section-decoration mx-auto"></div>
          <p className="text-white-50 mt-3 section-subtitle">
            Отзывы наших участников о работе в компании
          </p>
        </div>
        
        <div className="container-fluid fade-in-up stagger-2">
          {loading && (
            <div className="text-center text-white">
              <div className="loading-spinner">
                <div className="spinner-modern"></div>
              </div>
              <p className="mt-3 text-white-50">Загружаем отзывы...</p>
            </div>
          )}
          {error && (
            <div className="text-center">
              <p className="text-danger">{error}</p>
            </div>
          )}
          {!loading && !error && renderCarousel()}
        </div>
      </div>
      
      {/* Background decoration */}
      <div className="reviews-bg-decoration"></div>
    </section>
  );
};

export default Reviews;